import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  MapPin,
  Link,
  MessageSquare,
  Globe,
  Clock,
} from "lucide-react";

interface TimeZoneOption {
  value: string;
  label: string;
  offset: string;
}

interface InterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (data: {
    date: string;
    time: string;
    timeZone: string;
    candidateTimeZone: string;
    type: string;
    location: string;
    meetingLink: string;
    notes: string;
    sendEmail: boolean;
  }) => void;
  application?: {
    user?: {
      name: string;
    };
    job?: {
      title: string;
    };
  };
  isScheduling?: boolean;
}

interface InterviewFormData {
  date: string;
  time: string;
  type: string;
  location: string;
  meetingLink: string;
  notes: string;
  sendEmail: boolean;
  timeZone: string;
  candidateTimeZone: string;
}

const InterviewModal: React.FC<InterviewModalProps> = ({
  isOpen,
  onClose,
  onSchedule,
  application,
  isScheduling = false,
}) => {
  const [formData, setFormData] = useState<InterviewFormData>({
    date: "",
    time: "",
    type: "virtual",
    location: "",
    meetingLink: "",
    notes: "",
    sendEmail: true,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    candidateTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [convertedTimes, setConvertedTimes] = useState<{
    candidate: string;
    local: string;
  } | null>(null);
  const [timeZones, setTimeZones] = useState<TimeZoneOption[]>([]);

  // Base time zone definitions
  const baseTimeZones: Omit<TimeZoneOption, "offset">[] = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "America/Anchorage", label: "Alaska Time (AKT)" },
    { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
    { value: "UTC", label: "Coordinated Universal Time (UTC)" },
    { value: "Europe/London", label: "Greenwich Mean Time (GMT)" },
    { value: "Europe/Paris", label: "Central European Time (CET)" },
    { value: "Europe/Athens", label: "Eastern European Time (EET)" },
    { value: "Africa/Cairo", label: "Eastern European Time (EET)" },
    { value: "Africa/Nairobi", label: "East Africa Time (EAT)" },
    { value: "Europe/Moscow", label: "Moscow Standard Time (MSK)" },
    { value: "Asia/Dubai", label: "Gulf Standard Time (GST)" },
    { value: "Asia/Karachi", label: "Pakistan Standard Time (PKT)" },
    { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
    { value: "Asia/Dhaka", label: "Bangladesh Standard Time (BST)" },
    { value: "Asia/Bangkok", label: "Indochina Time (ICT)" },
    { value: "Asia/Hong_Kong", label: "Hong Kong Time (HKT)" },
    { value: "Asia/Singapore", label: "Singapore Time (SGT)" },
    { value: "Asia/Tokyo", label: "Japan Standard Time (JST)" },
    { value: "Asia/Seoul", label: "Korea Standard Time (KST)" },
    { value: "Australia/Perth", label: "Australian Western Time (AWT)" },
    { value: "Australia/Adelaide", label: "Australian Central Time (ACT)" },
    { value: "Australia/Sydney", label: "Australian Eastern Time (AET)" },
    { value: "Pacific/Auckland", label: "New Zealand Time (NZT)" },
    { value: "America/Sao_Paulo", label: "Brasilia Time (BRT)" },
    { value: "Africa/Lagos", label: "West Africa Time (WAT)" },
    {
      value: "Africa/Johannesburg",
      label: "South Africa Standard Time (SAST)",
    },
  ];

  // Compute UTC offset dynamically (DST-aware)
  const getOffset = (tz: string): string => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        timeZoneName: "shortOffset",
      });
      const parts = formatter.formatToParts(now);
      const offset =
        parts.find((p) => p.type === "timeZoneName")?.value || "UTC";
      return offset.replace("GMT", "UTC");
    } catch {
      return "UTC";
    }
  };

  // Generate full time zone list
  const generateTimeZones = (): TimeZoneOption[] => {
    return baseTimeZones
      .map((tz) => ({
        ...tz,
        offset: getOffset(tz.value),
      }))
      .sort((a, b) =>
        a.offset.localeCompare(b.offset, undefined, { numeric: true }),
      );
  };

  // Initialize and auto-refresh time zones
  useEffect(() => {
    setTimeZones(generateTimeZones());

    const interval = setInterval(
      () => {
        setTimeZones(generateTimeZones());
      },
      24 * 60 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      setFormData({
        date: tomorrow.toISOString().split("T")[0],
        time: "09:00",
        type: "virtual",
        location: "",
        meetingLink: "",
        notes: "",
        sendEmail: true,
        timeZone: userTimeZone,
        candidateTimeZone: userTimeZone,
      });
      setErrors({});
      setConvertedTimes(null);
    }
  }, [isOpen]);

  // Convert time between time zones
  const convertTimeZones = (
    date: string,
    time: string,
    // fromZone: string,
    toZone: string,
  ): string => {
    try {
      // Create date string in ISO format with timezone consideration
      const dateTimeString = `${date}T${time}:00`;

      // Create date object (this will be in local time, so we need to adjust)
      const localDate = new Date(dateTimeString);

      // Get the time in the target timezone
      return localDate.toLocaleTimeString("en-US", {
        timeZone: toZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error converting time zones:", error);
      return "Error";
    }
  };
  // Update converted times when form data changes
  useEffect(() => {
    if (
      formData.date &&
      formData.time &&
      formData.timeZone &&
      formData.candidateTimeZone &&
      formData.timeZone !== formData.candidateTimeZone
    ) {
      const candidateTime = convertTimeZones(
        formData.date,
        formData.time,
        formData.timeZone,
        // formData.candidateTimeZone
      );
      const localTime = convertTimeZones(
        formData.date,
        formData.time,
        formData.candidateTimeZone,
        // formData.timeZone
      );

      setConvertedTimes({
        candidate: candidateTime,
        local: localTime,
      });
    } else {
      setConvertedTimes(null);
    }
  }, [
    formData.date,
    formData.time,
    formData.timeZone,
    formData.candidateTimeZone,
  ]);

  // Suggest optimal times based on business hours
  const suggestOptimalTimes = () => {
    return ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"];
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = "Date is required";
    } else {
      const selectedDate = new Date(`${formData.date}T${formData.time}`);
      if (selectedDate <= new Date()) {
        newErrors.date =
          "Interview must be scheduled for a future date and time";
      }
    }

    if (!formData.time) {
      newErrors.time = "Time is required";
    }

    if (!formData.type) {
      newErrors.type = "Interview type is required";
    }

    if (formData.type === "virtual" && !formData.meetingLink) {
      newErrors.meetingLink = "Meeting link is required for virtual interviews";
    }

    if (formData.type === "in-person" && !formData.location) {
      newErrors.location = "Location is required for in-person interviews";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSchedule({
      date: formData.date,
      time: formData.time,
      timeZone: formData.timeZone,
      candidateTimeZone: formData.candidateTimeZone,
      type: formData.type,
      location: formData.location,
      meetingLink: formData.meetingLink,
      notes: formData.notes,
      sendEmail: formData.sendEmail,
    });

    onClose();
  };

  const handleInputChange = (
    field: keyof InterviewFormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const getCurrentTimeForZone = (timeZone: string): string => {
    return new Date().toLocaleTimeString("en-US", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Schedule Interview
            </h2>
            {application && (
              <p className="text-sm mt-1 text-gray-600 dark:text-gray-300">
                {application.user?.name} - {application.job?.title}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Time Zone Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Your Time Zone *
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={formData.timeZone}
                  onChange={(e) =>
                    handleInputChange("timeZone", e.target.value)
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {timeZones.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.offset})
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                Current: {getCurrentTimeForZone(formData.timeZone)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Candidate Time Zone *
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={formData.candidateTimeZone}
                  onChange={(e) =>
                    handleInputChange("candidateTimeZone", e.target.value)
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {timeZones.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.offset})
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
                Current: {getCurrentTimeForZone(formData.candidateTimeZone)}
              </p>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange("date", e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.date
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              />
              {errors.date && (
                <p className="text-red-500 text-xs mt-1">{errors.date}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Time ({formData.timeZone.split("/").pop()}) *
              </label>
              <input
                type="time"
                value={formData.time}
                onChange={(e) => handleInputChange("time", e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.time
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              />
              {errors.time && (
                <p className="text-red-500 text-xs mt-1">{errors.time}</p>
              )}
            </div>
          </div>

          {/* Time Conversion Display */}
          {convertedTimes && (
            <div className="p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Time Zone Conversion
                </span>
              </div>
              <div className="text-sm space-y-1 text-blue-800 dark:text-blue-200">
                <p>
                  <strong>Your time:</strong> {formData.time} (
                  {formData.timeZone.split("/").pop()})
                </p>
                <p>
                  <strong>Candidate's time:</strong> {convertedTimes.candidate}{" "}
                  ({formData.candidateTimeZone.split("/").pop()})
                </p>
              </div>
            </div>
          )}

          {/* Quick Time Suggestions */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Suggested Times
            </label>
            <div className="flex flex-wrap gap-2">
              {suggestOptimalTimes().map((time) => (
                <button
                  key={time}
                  type="button"
                  onClick={() => handleInputChange("time", time)}
                  className={`px-3 py-1 text-xs border rounded-lg transition-colors ${
                    formData.time === time
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>

          {/* Interview Type */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Interview Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange("type", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                errors.type
                  ? "border-red-500"
                  : "border-gray-300 dark:border-gray-600"
              }`}
            >
              <option value="virtual">Virtual</option>
              <option value="in-person">In-person</option>
              <option value="phone">Phone</option>
            </select>
            {errors.type && (
              <p className="text-red-500 text-xs mt-1">{errors.type}</p>
            )}
          </div>

          {/* Location (for in-person) */}
          {formData.type === "in-person" && (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Location *
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Enter interview location"
                  value={formData.location}
                  onChange={(e) =>
                    handleInputChange("location", e.target.value)
                  }
                  className={`w-full border rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                    errors.location
                      ? "border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                />
              </div>
              {errors.location && (
                <p className="text-red-500 text-xs mt-1">{errors.location}</p>
              )}
            </div>
          )}

          {/* Meeting Link (for virtual) */}
          {formData.type === "virtual" && (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
                Meeting Link *
              </label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="url"
                  placeholder="https://meet.google.com/..."
                  value={formData.meetingLink}
                  onChange={(e) =>
                    handleInputChange("meetingLink", e.target.value)
                  }
                  className={`w-full border rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${
                    errors.meetingLink
                      ? "border-red-500"
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                />
              </div>
              {errors.meetingLink && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.meetingLink}
                </p>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Additional Notes
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <textarea
                placeholder="Add any additional instructions or notes for the candidate..."
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                rows={3}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
          </div>

          {/* Email Notification */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="sendEmail"
              checked={formData.sendEmail}
              onChange={(e) => handleInputChange("sendEmail", e.target.checked)}
              className="w-4 h-4 rounded focus:ring-blue-500 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            />
            <label
              htmlFor="sendEmail"
              className="text-sm text-gray-700 dark:text-gray-300"
            >
              Send email notification with time zone information
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isScheduling}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isScheduling}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isScheduling ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Scheduling...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4" />
                  Schedule Interview
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InterviewModal;
